import React, { ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useExitAnim } from "../hooks/useExitAnim";

// =============================================================================
// MODAL
// =============================================================================
// PURPOSE: Reusable modal with smooth open + close animation, portal rendering,
// and focus trap. Reference implementation for the /animate modal-exit pattern.
//
// REQUIREMENTS:
// - ESC key to close
// - Click outside backdrop to close
// - Focus trap: Tab cycles within modal
// - Portal-rendered to document.body for z-index control
// - Smooth entrance (anim-modal-in) AND exit (anim-modal-out) via useExitAnim —
//   transform rides a NON-glass wrapper so the .detail-overlay glass never blurs.
// =============================================================================

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  onSubmit?: () => void; // Cmd/Ctrl+Enter handler
  maxWidth?: string; // Override max-width class (default: max-w-7xl)
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, onSubmit, maxWidth }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  // Keep mounted through the 200ms exit so anim-modal-out actually plays.
  const { mounted, closing } = useExitAnim(isOpen, 200);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [isOpen, onClose]);

  // Handle Cmd/Ctrl+Enter to submit
  useEffect(() => {
    if (!isOpen || !onSubmit) return;

    const handleSubmitShortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        event.preventDefault();
        onSubmit();
      }
    };

    document.addEventListener("keydown", handleSubmitShortcut);
    return () => document.removeEventListener("keydown", handleSubmitShortcut);
  }, [isOpen, onSubmit]);

  // Focus trap: keep tab cycling within modal
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const modal = modalRef.current;
    const focusableElements = modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
    };

    modal.addEventListener("keydown", handleTabKey as EventListener);

    // Focus the first focusable element when modal opens
    firstElement?.focus();

    return () => {
      modal.removeEventListener("keydown", handleTabKey as EventListener);
    };
  }, [isOpen]);

  if (!mounted) return null;

  return createPortal(
    <div
      className={`fixed inset-0 bg-black/30 flex items-center justify-center z-[9999] backdrop-blur-md ${
        closing ? "anim-backdrop-out" : "anim-backdrop-in"
      }`}
      onClick={onClose}
    >
      {/* NON-glass wrapper carries the transform; the glass panel below never repaints. */}
      <div
        className={`w-[90vw] ${maxWidth || "max-w-7xl"} m-4 ${
          closing ? "anim-modal-out" : "anim-modal-in"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          className="detail-overlay rounded-xl shadow-2xl w-full max-h-[90vh] p-6 relative border border-black/[0.08] overflow-y-auto"
        >
          <div className="flex justify-between items-center mb-4 border-b border-black/[0.06] pb-2">
            <h2 id="modal-title" className="text-xl font-bold text-glass">
              {title}
            </h2>
            <button
              onClick={onClose}
              className="text-glass-muted hover:text-glass-secondary transition-colors"
              aria-label="Close modal"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Modal;
